import clsx from 'clsx';
import { ReactNode } from 'react';
import {
    ExclamationTriangleIcon,
    InformationCircleIcon,
    NoSymbolIcon,
} from '@heroicons/react/20/solid';
import type { IconType } from './Chip';

export type BannerTone = 'info' | 'warning' | 'danger' | 'neutral-strong';

const TONE: Record<BannerTone, { box: string; icon: IconType; action: string }> = {
    info: { box: 'bg-tone-info-bg text-tone-info-fg', icon: InformationCircleIcon, action: 'text-tone-info-fg' },
    warning: { box: 'bg-tone-warning-bg text-tone-warning-fg', icon: ExclamationTriangleIcon, action: 'text-tone-warning-fg' },
    danger: { box: 'bg-tone-danger-bg text-tone-danger-fg', icon: ExclamationTriangleIcon, action: 'text-tone-danger-fg' },
    'neutral-strong': { box: 'bg-inverse text-on-inverse', icon: NoSymbolIcon, action: 'text-on-inverse' },
};

export interface InlineBannerProps {
    tone: BannerTone;
    children: ReactNode;
    icon?: IconType;
    action?: { label: string; onClick: () => void };
    className?: string;
}

/** In-context message (form error, offline, paused). Toasts are for confirmations. */
export const InlineBanner = ({ tone, children, icon, action, className }: InlineBannerProps) => {
    const Icon = icon ?? TONE[tone].icon;
    return (
        <div
            role={tone === 'danger' ? 'alert' : 'status'}
            className={clsx('flex items-start gap-3 rounded-control p-3 text-small', TONE[tone].box, className)}
        >
            <Icon className="mt-0.5 size-5 shrink-0" aria-hidden="true" />
            <div className="min-w-0 flex-1">{children}</div>
            {action && (
                <button
                    type="button"
                    onClick={action.onClick}
                    className={clsx(
                        'relative -my-2 inline-flex min-h-control shrink-0 items-center px-1 font-semibold underline underline-offset-2',
                        TONE[tone].action
                    )}
                >
                    {action.label}
                </button>
            )}
        </div>
    );
};
